import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api } from "@/helper/api";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import PasswordInput from "@/components/forms/inputs/passwordInput";
import { useForm } from "react-hook-form";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";

import styles from "@/components/forms/resetPasswordForm.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import { useSecurePasswordGuideLink } from "@/helper/customHooks/useSecurePasswordGuideLink";

interface Props {
  title?: string;
}

const ResetPasswordForm: FC<Props> = () => {
  const securePasswordGuideLink = useSecurePasswordGuideLink();
  const { handleError, errorModal } = useErrorModal(undefined, {
    helpLinksByField: { password: [securePasswordGuideLink] },
  });

  const { handleSubmit, control, watch } = useForm({
    mode: "onChange",
    shouldFocusError: false,
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField({
      fieldOrder: ["currentPassword", "newPassword", "confirmNewPassword"],
    });
  const newPassword = watch("newPassword");

  const passwordRegex = new RegExp(
    /^(?=.*[0-9])(?=.*[!=?\-_+/&$#*.,:;@]).{10,}$/
  );
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onSubmit = (data: any) => {
    const result = {
      current_password: data.currentPassword,
      new_password: data.newPassword,
    };

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      entity: "user/change_own_password",
      method: "put",
      successHandler: () => {
        navigate("/login");
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      submitData: result,
      config: config,
    });
  };

  return (
    <>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t("reset_password")}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <form
            onSubmit={handleSubmit(onSubmit, (errors) => {
              focusFirstInvalidField(errors);
            })}
            className={styles.formStyle}
            noValidate
          >
            <PasswordInput
              validationTargetRef={registerValidationTarget("currentPassword")}
              id={t("current_password")}
              label={t("current_password")}
              title={t("current_password")}
              dynamicLabel={false}
              control={control}
              name={"currentPassword"}
              required={true}
              requiredErrorMessage={t("field_required")}
              labelClassName={styles.passwordInput__label}
              inputClassName={styles.passwordInput__input}
            />
            <Divider component={"span"} />
            <PasswordInput
              validationTargetRef={registerValidationTarget("newPassword")}
              id={t("new_password")}
              title={t("new_password")}
              // label={t("new_password")}
              dynamicLabel={false}
              control={control}
              name={"newPassword"}
              required={true}
              requiredErrorMessage={t("field_required")}
              pattern={passwordRegex}
              patternErrorMessage={t("password_requirements")}
              labelClassName={styles.passwordInput__label}
              inputClassName={styles.passwordInput__input}
              label={
                <div>
                  <span style={{ display: "block" }}>
                    {t("new_password")}{" "}
                  </span>
                  <span style={{ fontSize: "0.8rem" }}>
                    {t("password_requirements")}
                  </span>
                </div>
              }
            />

            <PasswordInput
              validationTargetRef={registerValidationTarget(
                "confirmNewPassword",
              )}
              id={t("confirm_password")}
              label={t("confirm_password")}
              title={t("confirm_password")}
              dynamicLabel={false}
              control={control}
              name={"confirmNewPassword"}
              required={true}
              requiredErrorMessage={t("field_required")}
              validationRule={newPassword}
              validationRuleErrorMessage={t("password_dont_match")}
              labelClassName={styles.passwordInput__label}
              inputClassName={styles.passwordInput__input}
            />
            <GenericButton className={styles.submitButton} type="submit">
              {t("save")}
            </GenericButton>
          </form>
        </AccordionDetails>
      </Accordion>
      {errorModal}
    </>
  );
};

export default ResetPasswordForm;
